/**
 * AssetWrx Design System — single source of truth for colour.
 *
 * ▸ MASTER:        `palette`              — primitive ramps (Direction A: Refined Operator)
 * ▸ MASTER:        `semantic` / `useSemanticColors()` — mode-adaptive aliases
 * ▸ MASTER (hook): `useUiPalette()`       — scheme-aware tokens for components
 * ▸ DEPRECATED:    `colors`               — static aliases; re-routed to derive
 *                                            from `palette` so the system has
 *                                            ONE underlying source of truth.
 *                                            Existing callsites keep compiling;
 *                                            new code should consume `palette`
 *                                            or `useSemanticColors()` directly.
 *
 * The `colors` surface stays available for backward compatibility, but every
 * value now derives from `palette`. There are no longer two colour systems.
 */


/**
 * AssetWRX primitive colour palette — premium enterprise.
 *
 * Built from the actual brand mark: deep ink (`#101820`), warm amber spark
 * (`#D99A1E`), and a slate carrying secondary type (`#52616B`). The result
 * reads as ink-and-paper enterprise — confident, calm, type-led — closer
 * to Stripe Docs or a private-bank terminal than a consumer SaaS landing.
 *
 * The two identity colours play distinct roles:
 *
 *   ▸ `brand` (ink)   — THE primary. Buttons, headings, dark surfaces,
 *                       icons. Carries weight. Used decisively, not loudly.
 *   ▸ `signal` (amber)— The spark. Focus rings, brand mark, "alive" moments,
 *                       small selection cues. The brass dot in a steel room.
 *                       Used sparingly so it stays special.
 *   ▸ `neutral`       — Cool slate canvas. 50–200 surfaces, 300–500 borders,
 *                       600–900 type.
 *   ▸ `success`       — Emerald, muted. "In-spec", not "go".
 *   ▸ `warning`       — Amber-deep. Reads differently from `signal` so the
 *                       brand spark and an operational warning never collide.
 *   ▸ `danger`        — Rose-red, restrained.
 *
 * Raw values only; product code should consume `useSemanticColors()` /
 * `useUiPalette()`, which derive from this file.
 */
export const palette = {
  // Pure values — used for inverse surfaces and absolute white/black.
  white: '#FFFFFF',
  black: '#000000',

  // Cool slate neutrals tuned to sit alongside the brand ink (#101820).
  // 50–200 surfaces, 300–500 borders/disabled, 600–900 type.
  neutral: {
    0:   '#FFFFFF',
    50:  '#F7F8F9',
    100: '#EEF0F2',
    200: '#DCE0E4',
    300: '#B8BFC6',
    400: '#8A929B',
    500: '#52616B', // brand slate — secondary text in the logo
    600: '#3E4A52',
    700: '#2A333A',
    // Dark-mode field/chip fill: one step above the dark card (800) that keeps
    // tertiary text (400) at 4.68:1, where 700 would drop it to 4.0:1.
    750: '#212930',
    800: '#1A2128',
    900: '#101820', // BRAND INK — the canonical brand colour
    950: '#080C10',
  },

  // BRAND ink. The brand is a colour, not a hue — it's the inky near-black
  // from the logo. 900 IS the brand. 500 is its slate secondary. Use ink
  // for primary buttons, headings, dark surfaces, icons. Do not tint.
  brand: {
    50:  '#F7F8F9',
    100: '#EEF0F2',
    200: '#DCE0E4',
    300: '#B8BFC6',
    400: '#8A929B',
    500: '#52616B', // brand slate (the logo's secondary)
    600: '#3E4A52',
    700: '#2A333A',
    800: '#1A2128',
    900: '#101820', // THE brand colour — the ink itself
  },

  // SIGNAL amber — the warm spark from the logo dot. Reserved for the brand
  // mark, focus rings, selection, and rare "alive" moments. NEVER a section
  // background. Treat like polished brass — small, intentional, memorable.
  signal: {
    50:  '#FBF6EC',
    100: '#F6E9CC',
    200: '#EFD497',
    300: '#E7BB5E',
    400: '#DFA535',
    500: '#D99A1E', // CANONICAL brand spark — the logo dot
    600: '#B07B14',
    700: '#825A0E',
    800: '#553B08',
    900: '#2D1F04',
  },

  // Success — emerald, muted. "In-spec", not "go".
  success: {
    50:  '#EEF7F1',
    100: '#D1ECD9',
    200: '#A3D8B5',
    300: '#6FBF8C',
    400: '#3FA567',
    500: '#1F7A4A', // restrained emerald — reads "verified" not "neon"
    600: '#155A37',
    700: '#0F4329',
    800: '#0A2E1C',
    900: '#051A10',
  },

  // Warning — amber-deep. Visually distinct from the brand `signal` spark so
  // an operational warning never reads as brand chrome. Slightly orange-leaning.
  warning: {
    50:  '#FDF2E7',
    100: '#FADFC2',
    200: '#F4BC83',
    300: '#EE9744',
    400: '#D67A1F',
    500: '#B45D0E', // burnt amber — clearly a warning, not the brand spark
    600: '#8D4708',
    700: '#693505',
    800: '#452303',
    900: '#241201',
  },

  // Danger — rose-red, restrained. Reads "stop" without shouting.
  danger: {
    50:  '#FBEEEC',
    100: '#F5D2CD',
    200: '#E9A299',
    300: '#DC7065',
    400: '#CC4738',
    500: '#B23121', // confident not feverish
    600: '#8D2317',
    700: '#691810',
    800: '#450F0A',
    900: '#240705',
  },

  // Info — ink-tinted. Pulled inward to the brand so info banners feel like
  // the product, not a third colour. (We get one accent — that's signal.)
  info: {
    50:  '#F7F8F9',
    100: '#EEF0F2',
    500: '#52616B',
    600: '#3E4A52',
    700: '#2A333A',
  },

  // Accent for asset IDs and inline code (mono surfaces). Neutral by design.
  accent: {
    50:  '#F7F8F9',
    100: '#EEF0F2',
    500: '#3E4A52',
    600: '#2A333A',
    700: '#1A2128',
  },

  // Asset-type accents. Drawn from the ramps so they shift with the palette.
  // The brand `signal` amber is reserved for vehicles — the asset type the
  // detail badge needs to feel most distinct.
  assetAccent: {
    car:       '#D99A1E', // signal.500 — vehicles wear the brand spark
    property:  '#1F7A4A', // success.500 — buildings & land
    equipment: '#B45D0E', // warning.500 — machinery & tools
    other:     '#8A929B', // neutral.400 — everything else
  },
} as const;

/**
 * Static semantic colour aliases.
 *
 * @deprecated Prefer `palette` (primitive ramps) + `useSemanticColors()` /
 *   `useUiPalette()` (mode-adaptive). This surface is retained as an alias
 *   for existing callsites; every value is derived from `palette` so the
 *   two paths can never diverge. New code should not import from `colors`.
 */
export const colors = {
  brand: {
    dark:        palette.neutral[900],
    slate:       palette.neutral[800],
    steel:       palette.neutral[600],
    mist:        palette.neutral[50],
    white:       palette.white,
    accent:      palette.signal[500],
    action:      '#2563EB',
    verified:    '#16A34A',
    fault:       '#DC2626',
    borderLight: palette.neutral[200],
  },
  primaryLight:        '#DBEAFE',
  primary:             '#2563EB',
  primaryDark:         '#1D4ED8',
  primaryDarker:       '#1E40AF',
  successLight:        '#DCFCE7',
  /**
   * The flat status aliases are the ramp's 500 step — the values the design
   * direction actually declares (success #1F7A4A, danger #B23121).
   *
   * They used to be #16A34A / #DC2626, which are steps of no ramp in this
   * system: brighter, more saturated leftovers from before the palette existed.
   * Measured in the browser they failed as text almost everywhere they landed —
   * #16A34A read 2.76:1 on a sunken chip and 3.30:1 on a white card.
   *
   * They sit at the 600 step, not the 500: these flat aliases are overwhelmingly
   * consumed as TEXT (status labels, sync badges, condition readings), and the
   * 500s measured 4.46:1 on a sunken chip — just under AA. A fill wants
   * `p.success` / `p.danger` from the semantic layer instead.
   */
  success:             palette.success[600],
  /**
   * The `*Dark` aliases are the TEXT-SAFE step of each status ramp.
   *
   * They used to be three hand-picked hex values that were not steps of any
   * ramp at all (#15803D / #B45309 / #991B1B), sitting between the 500 and 600
   * stops. That is how a status label ended up at 4.44:1 on its own soft tint —
   * a colour nobody had ever measured, because it belonged to no scale.
   *
   * Now they ARE the 600 steps, which clear AA on every surface the product
   * puts them on (soft tint 6.1-7.8:1, card 6.9-8.8:1, canvas 6.3-8.1:1).
   * Anything that needs the lighter mid-ramp value for a FILL should take
   * `colors.success` / `.warning` / `.danger`; these three are for things that
   * have to be read.
   */
  successDark:         palette.success[600],
  warningLight:        '#FEF3C7',
  warning:             palette.signal[500],
  warningDark:         palette.warning[600],
  dangerLight:         '#FEE2E2',
  danger:              palette.danger[500],
  dangerDark:          palette.danger[600],
  dangerButtonHover:   '#B91C1C',
  dangerButtonPressed: '#991B1B',
  dangerAccent:        '#FCA5A5',
  white:               palette.white,
  neutral: {
    0:   palette.neutral[0],
    50:  palette.neutral[50],
    100: palette.neutral[100],
    200: palette.neutral[200],
    300: palette.neutral[300],
    400: palette.neutral[400],
    500: palette.neutral[500],
    600: palette.neutral[600],
    700: palette.neutral[700],
    800: palette.neutral[800],
    900: palette.neutral[900],
  },
  text: {
    primary:     palette.neutral[900],
    heading:     palette.neutral[900],
    secondary:   palette.neutral[600],
    muted:       palette.neutral[500],
    placeholder: palette.neutral[400],
    disabled:    palette.neutral[400],
    inverse:     palette.white,
    error:       palette.danger[800],
    success:     palette.success[700],
  },
  surface: {
    background: palette.neutral[50],
    card:       palette.white,
    elevated:   palette.white,
    subtle:     palette.neutral[100],
    dark:       palette.neutral[900],
  },
  /**
   * Aligned with `lightSemantic.border` — these were a THIRD ramp under the same
   * names, one step lighter at every rung (`default` was neutral[300], 1.86:1).
   * So `colors.border.default` and the semantic `border.default` both read as
   * "the default border" and resolved to different colours, and the lighter one
   * did not clear the 3:1 a control boundary needs.
   *
   * Same defect as the duplicate radius scales: same names, different values,
   * both live, nothing to catch it.
   */
  border: {
    subtle:  palette.neutral[200],   // 1.33:1 — decorative, cards + dividers
    default: palette.neutral[400],   // 3.15:1 — control boundaries
    strong:  palette.neutral[500],   // 6.40:1
    focus:   palette.brand[500],
    danger:  palette.danger[500],
    success: palette.success[500],
  },
  icon: {
    default: palette.neutral[600],
    muted:   palette.neutral[400],
    primary: palette.brand[500],
    inverse: palette.white,
  },
  // Focus ring derives from signal.500 (217, 154, 30) — the warm amber spark.
  // Kept as rgba so consumers that compose it on tinted surfaces keep the
  // soft halo. The amber is the only colour the eye should chase.
  focusRing:     'rgba(217, 154, 30, 0.18)',
  primarySubtle: '#DBEAFE',
  successSubtle: palette.success[100],
  warningSubtle: palette.warning[100],
  dangerSubtle:  palette.danger[100],
} as const;

export type Colors = typeof colors;

export type PaletteScale = keyof typeof palette;
export type NeutralStep = keyof typeof palette.neutral;
export type BrandStep   = keyof typeof palette.brand;
